import { FormInput, FormSelect } from '@systemeio/ui'
import { ErrorResponseContentInterface } from '@systemeio/util'
import { WiseRecipientFormFieldTypesEnum } from 'modules/profile/affiliate-settings/enums/wise-recipient-form-field-types-enum'
import { useWiseRecipientApi } from 'modules/profile/affiliate-settings/hooks/use-wise-recipient-api'
import { WiseRecipientApiInterface } from 'modules/profile/affiliate-settings/types/affiliate-settings-interface'
import {
  WiseRecipientDetailsFieldInterface,
  WiseRecipientDetailsInterface,
  WiseRecipientDetailsItemInterface,
} from 'modules/profile/affiliate-settings/types/wise-details-interface'
import { useEffect, useState } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'

interface AdditionalFormFieldsProps {
  formData: WiseRecipientDetailsInterface | null
  setFormData: React.Dispatch<React.SetStateAction<WiseRecipientDetailsInterface | null>>
  state: WiseRecipientApiInterface
  onChangeDetails: (key: string, value: string) => void
  fieldErrors: ErrorResponseContentInterface['fields']
  onChangeRecipientDetailsType: (value: string) => void
  resetFormErrors: () => void
  updateFormDetails: (details: Record<string, string>) => void
}

export function AdditionalFormFields({
  formData,
  setFormData,
  onChangeDetails,
  state,
  fieldErrors,
  onChangeRecipientDetailsType,
  resetFormErrors,
  updateFormDetails,
}: AdditionalFormFieldsProps) {
  const { t } = useLocoTranslation()
  const isTypeOfRecipientDetailsSelect = !!formData
  const [activeForm, setActiveForm] = useState<WiseRecipientDetailsItemInterface | undefined>()
  const [isActiveFormFetching, setIsActiveFormFetching] = useState(false)
  const typeOfRecipientDetailsSelectData = isTypeOfRecipientDetailsSelect
    ? Object.keys(formData).map(formTypeKey => {
        return {
          id: formData[formTypeKey].type,
          caption: formData[formTypeKey].title,
        }
      })
    : []

  const { getCurrentWiseRecipientDetails } = useWiseRecipientApi()

  const handleFormSelectChange = async (
    field: WiseRecipientDetailsFieldInterface,
    data: string,
  ) => {
    resetFormErrors()
    onChangeDetails(field.group[0].key, data)
    if (field.group[0].refreshRequirementsOnChange) {
      const updatedDetailsFields = {
        ...state.details,
        [field.group[0].key]: data,
      }
      setIsActiveFormFetching(true)
      const updatedState = {
        ...state,
        details: updatedDetailsFields,
      }
      const responseData = await getCurrentWiseRecipientDetails(state.currency, updatedState)
      const formFields = responseData[state.recipientDetailsType].fields.map(
        (field: WiseRecipientDetailsFieldInterface) => field.group[0].key,
      )
      Object.keys(state.details).forEach(fieldKey => {
        if (!formFields.includes(fieldKey)) delete updatedDetailsFields[fieldKey]
      })
      setFormData(responseData)
      updateFormDetails(updatedDetailsFields)
      setIsActiveFormFetching(false)
    }
  }

  useEffect(() => {
    if (formData && formData[state.recipientDetailsType]) {
      setActiveForm(formData[state.recipientDetailsType])
    }
  }, [formData, state.recipientDetailsType])

  return (
    <>
      {isTypeOfRecipientDetailsSelect && (
        <FormSelect
          data={typeOfRecipientDetailsSelectData}
          label={t('dashboard.user_settings.affiliate_commissions.wire_table.recipient_type_title')}
          placeholder={t(
            'dashboard.user_settings.affiliate_commissions.wire_table.recipient_type_placeholder',
          )}
          value={activeForm?.type}
          onChange={data => {
            const updatedRecipientDetailsType = Object.keys(formData).find(
              formTypeKey => formData[formTypeKey].type === data,
            )
            if (updatedRecipientDetailsType) {
              onChangeRecipientDetailsType(formData[updatedRecipientDetailsType].type)
              setActiveForm(formData[updatedRecipientDetailsType])
            }
          }}
          withoutCloseIcon
          required
        />
      )}
      {activeForm?.fields.map(field =>
        getFormField(
          field,
          onChangeDetails,
          handleFormSelectChange,
          state,
          fieldErrors,
          isActiveFormFetching,
        ),
      )}
    </>
  )
}

export function getFormField(
  field: WiseRecipientDetailsFieldInterface,
  onChange: (key: string, value: string) => void,
  onChangeDetailsWithFormUpdate: (
    field: WiseRecipientDetailsFieldInterface,
    data: string,
  ) => Promise<void>,
  state: WiseRecipientApiInterface,
  fieldErrors: ErrorResponseContentInterface['fields'],
  isActiveFormFetching: boolean,
) {
  switch (field.group[0].type) {
    case WiseRecipientFormFieldTypesEnum.select:
    case WiseRecipientFormFieldTypesEnum.radio:
      return (
        <FormSelect
          data={field.group[0].valuesAllowed?.map(el => {
            return { ...el, id: el.key, caption: el.name }
          })}
          label={field.group[0].name}
          value={state.details[field.group[0].key] || ''}
          onChange={data => {
            if (data) {
              onChangeDetailsWithFormUpdate(field, data)
            }
          }}
          withoutCloseIcon
          required={field.group[0].required}
          error={fieldErrors[field.group[0].key]?.join('')}
          disabled={isActiveFormFetching}
          key={field.group[0].key}
        />
      )
    case WiseRecipientFormFieldTypesEnum.date:
    case WiseRecipientFormFieldTypesEnum.text:
      return (
        <FormInput
          value={state.details[field.group[0].key] || ''}
          label={field.group[0].name}
          onChange={e => onChange(field.group[0].key, e.target.value)}
          required={field.group[0].required}
          error={fieldErrors[field.group[0].key]?.join('')}
          disabled={isActiveFormFetching}
          key={field.group[0].key}
        />
      )
    default:
      break
  }
}
